import { Button, useTheme } from '@material-ui/core'
import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  SettlementsKPIsResponseModel,
  SettlementsStatusKPIsResponseModel
} from '../../../../api/Argentia/endpoints/settlements/model/settlements'
import { envConfig } from '../../../../config/envConfig'
import { capKpiValue } from '../../../../helpers/numberHelper'
import { SettlementKpiBlock, SettlementKpiBlockRow } from '../settlementKpiBlock/settlementKpiBlock'
import { SettlementKpiType } from '../settlementKpiRightContent/settlementKpiRightContent'

interface SettlementKpiBlocksProps {
  /** Resumen de todas las liquidaciones de la ejecución por estado */
  statusKpis?: SettlementsStatusKPIsResponseModel
  isLoadingStatusKpis?: boolean
  kpis?: SettlementsKPIsResponseModel
  isLoadingKpis?: boolean
  /** KPI actualmente seleccionado */
  activeKpi?: SettlementKpiType | null
  onKpiClick?: (kpiType: SettlementKpiType) => void
  /** Si no se informa (ejecución cerrada) no se muestra el botón de regenerar */
  onRegenerateClick?: () => void
  /** La ejecución está procesando: el bloque En curso muestra el seguimiento y sus acciones */
  isProcessing?: boolean
  /** Procesando pero el polling se detuvo por máximo de reintentos */
  isPaused?: boolean
  /** Terminó de procesar mientras se seguía en esta pantalla: invita a actualizar la información */
  isFinished?: boolean
  onCheckAgainClick?: () => void
  onViewProgressClick?: () => void
  onRefreshClick?: () => void
  onViewDetailClick?: () => void
}

/** Validar listado: la fase activa es "Liquidaciones sin facturar" */
export const SettlementKpiBlocks: React.FC<SettlementKpiBlocksProps> = ({
  statusKpis,
  isLoadingStatusKpis = false,
  kpis,
  isLoadingKpis = false,
  activeKpi,
  onKpiClick,
  onRegenerateClick,
  isProcessing = false,
  isPaused = false,
  isFinished = false,
  onCheckAgainClick,
  onViewProgressClick,
  onRefreshClick,
  onViewDetailClick
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'settlements'])
  const theme = useTheme()
  const maxDisplayValue = envConfig.kpiConfig.maxDisplayValue

  // Facturadas: liquidaciones de la ejecución que ya no están pendientes ni en curso (las anuladas cuentan como emitidas)
  const billedSettlements = statusKpis
    ? (statusKpis.open ?? 0) +
      (statusKpis.closed ?? 0) +
      (statusKpis.cancelled ?? 0) +
      (statusKpis.error ?? 0)
    : undefined

  return (
    <>
      <SettlementKpiBlock
        isActive
        isLoading={isLoadingStatusKpis}
        title={t('settlements:components.kpiBlocks.unbilled.title')}
        value={capKpiValue(statusKpis?.pending, maxDisplayValue)}
        linkLabel={t('settlements:components.kpiBlocks.unbilled.viewAll')}
        onLinkClick={onKpiClick ? () => onKpiClick('all') : undefined}
        isLinkSelected={activeKpi === 'all'}
        footer={
          onRegenerateClick && (
            <Button variant="text" color="primary" onClick={onRegenerateClick}>
              {t('settlements:components.kpiBlocks.unbilled.regenerate')}
            </Button>
          )
        }
      >
        <SettlementKpiBlockRow
          isLoading={isLoadingKpis}
          label={t('settlements:components.kpiBlocks.unbilled.billable')}
          value={capKpiValue(kpis?.totalSettlementsValidated, maxDisplayValue)}
          color={theme.palette.success.main}
          onClick={onKpiClick ? () => onKpiClick('validated') : undefined}
          isSelected={activeKpi === 'validated'}
        />
        <SettlementKpiBlockRow
          isLoading={isLoadingKpis}
          label={t('settlements:components.kpiBlocks.unbilled.nonBillable')}
          value={capKpiValue(kpis?.totalSettlementsWithObjections, maxDisplayValue)}
          color={theme.palette.warning.main}
          onClick={onKpiClick ? () => onKpiClick('withObjections') : undefined}
          isSelected={activeKpi === 'withObjections'}
        />
      </SettlementKpiBlock>
      <SettlementKpiBlock
        isLoading={isLoadingStatusKpis}
        title={t('settlements:components.kpiBlocks.inProgress.title')}
        value={capKpiValue(statusKpis?.ongoing, maxDisplayValue)}
        caption={t('settlements:components.kpiBlocks.inProgress.caption')}
        processingMessage={
          isProcessing || isFinished
            ? t('settlements:components.kpiBlocks.inProgress.' + (isFinished ? 'finished' : isPaused ? 'paused' : 'processing'))
            : undefined
        }
        isProcessingPaused={isPaused || isFinished}
        footer={
          (isProcessing || isFinished) && (
            <>
              {isPaused
                ? onCheckAgainClick && (
                    <Button variant="text" color="primary" onClick={onCheckAgainClick}>
                      {t('settlements:components.kpiBlocks.inProgress.checkAgain')}
                    </Button>
                  )
                : onRefreshClick && (
                    <Button variant="text" color="primary" onClick={onRefreshClick}>
                      {t('settlements:components.kpiBlocks.inProgress.refresh')}
                    </Button>
                  )}
              {!isFinished && onViewProgressClick && (
                <Button variant="outlined" color="primary" onClick={onViewProgressClick}>
                  {t('settlements:components.kpiBlocks.inProgress.viewProgress')}
                </Button>
              )}
            </>
          )
        }
      />
      <SettlementKpiBlock
        isLoading={isLoadingStatusKpis}
        title={t('settlements:components.kpiBlocks.billed.title')}
        value={capKpiValue(billedSettlements, maxDisplayValue)}
        caption={t('settlements:components.kpiBlocks.billed.caption')}
        footer={
          onViewDetailClick && (
            <Button
              variant="text"
              color="primary"
              disabled={!kpis?.totalInvoicedSettlements}
              onClick={onViewDetailClick}
            >
              {t('settlements:components.kpiBlocks.billed.viewDetail')}
            </Button>
          )
        }
      />
    </>
  )
}
